// sound5/engine.js: builds the graph on one context and drives it from the
// film state. Everything is scheduled on the audio clock (ctx.currentTime),
// so an OfflineAudioContext stepped with suspend() renders exactly what a
// visitor would hear.
import { makeRng, makeConstant, Knob, clamp, lerp, num } from './util.js';
import { REGS, BELLS, PROG } from './music.js';
import { Bank } from './instruments.js';
import { Master } from './master.js';
import { Bus, makeLayers } from './voices.js';
import { makeCluster } from './tension.js';
import { Shots, Sequencer } from './events.js';
import { Cues } from './cues.js';
import { targets } from './scene.js';

// voice caps and costs per device tier
export const TIERS = {
  high: { organNotes: 8, choirNotes: 5, choirUnison: 3, haloNotes: 3, clusterNotes: 9, shots: 24, bankSr: 32000, work: 4 },
  mid: { organNotes: 5, choirNotes: 4, choirUnison: 2, haloNotes: 2, clusterNotes: 6, shots: 12, bankSr: 24000, work: 3 },
  low: { organNotes: 4, choirNotes: 3, choirUnison: 1, haloNotes: 1, clusterNotes: 4, shots: 8, bankSr: 24000, work: 2 },
};

// bus levels (the mix)
export const MIX = {
  organ: 1, choir: 1, halo: 1, drone: 0.8, cloud: 0.5, air: 0.2, cluster: 1,
  piano: 0.5, bells: 0.4, heart: 0.5, tick: 0.35, motif: 0.6, fx: 1, ui: 0.35,
};

export class Engine {
  constructor(ctx, { tier = 'high', seed = 1977 } = {}) {
    this.ctx = ctx;
    this.tier = TIERS[tier] ? tier : 'high';
    this.cfg = TIERS[this.tier];
    this.rng = makeRng(seed);
    this.regs = REGS;
    this.reg = 'principal';
    this.brand = 'Starlex';
    const c = ctx;
    this.master = new Master(c, { tier: this.tier });
    let sr = this.cfg.bankSr;
    try { c.createBuffer(1, 1, sr); } catch (e) { sr = c.sampleRate; }
    this.bank = new Bank(c, makeRng(seed + 1), sr);
    // the global pitch bend (cents), summed into every oscillator's detune
    this.bend = makeConstant(c, 0);
    this.kBend = new Knob(this.bend.param, 0, 0, 0.5);
    this.canUnbend = !!c.createConstantSource; // selective disconnect is safe there
    this.L = makeLayers(this);
    this.L.chords = [this.L.organL, this.L.choirL, this.L.haloL];
    this.cluster = makeCluster(this);
    const fixed = (dry, send, toFx) => {
      const b = new Bus(this, toFx ? 0 : dry, send);
      if (toFx) { const g = c.createGain(); g.gain.value = dry; b.g.connect(g); g.connect(this.master.fx); }
      return b;
    };
    this.B = {
      piano: new Bus(this, 0.9, 0.35), bells: new Bus(this, 0.5, 0.9), heart: new Bus(this, 1, 0.12),
      tick: new Bus(this, 0.8, 0.3), motif: fixed(0.8, 0.6, true), fx: fixed(1, 0.6, true), ui: fixed(1, 0.15, true),
    };
    for (const k of ['motif', 'fx', 'ui']) this.B[k].k.jump(MIX[k], 0);
    this.shots = new Shots(this, this.cfg.shots);
    this.seq = new Sequencer(this);
    this.cues = new Cues(this);
    // memory the scene reads (audio-clock seconds, -1 = never)
    this.igniteAt = -1;
    this.lit = false;
    this.brandAt = -1;
    this.darkAt = -1;
    this.visit4 = false;
    // prefetch in film order
    this.bank.want('thump', [0]);
    this.bank.want('swell', [0]);
    this.bank.want('bell', BELLS);
    this.bank.want('piano', [69, 74, 76, 78, 66, 71, 67, 81, 79, 62, 64]);
    this.bank.want('tick', [0]);
  }

  bendTo(param) { try { this.bend.node.connect(param); } catch (e) { /* ignore */ } }
  unbend(param) { if (this.canUnbend) try { this.bend.node.disconnect(param); } catch (e) { /* ignore */ } }

  memory(now, st) {
    const lit = this.lit || st.igniteT >= 0 || st.chapter > 0;
    if (this.darkAt >= 0) {
      const since = now - this.darkAt;
      const back = st.chapter < 3 || (st.chapter === 3 && st.progress < 0.9);
      if (back || (st.chapter >= 4 && since > 2.5)) {
        this.darkAt = -1;
        this.master.kDuck.go(1, now, 0.25);
      }
    }
    if (st.chapter === 4 && this.darkAt < 0) {
      if (!this.visit4) { this.visit4 = true; this.seq.motifAt = now + 1.2; if (this.brandAt !== -2) this.brandAt = now + 1.2; }
    } else if (st.chapter < 4) this.visit4 = false;
    return {
      lit,
      sinceIgnite: this.igniteAt >= 0 ? now - this.igniteAt : 1e9,
      silentIgnite: lit && this.igniteAt < 0,
      sinceBrand: this.brandAt === -2 ? 1e9 : this.brandAt >= 0 && now >= this.brandAt ? now - this.brandAt : -1,
      dark: this.darkAt >= 0,
    };
  }

  update(dt, st) {
    const now = this.ctx.currentTime, L = this.L, B = this.B, m = this.master;
    this.brand = st.brand || this.brand;
    const T = targets(st, this.memory(now, st));
    this.T = T;
    const up = (bus, v, tcUp, tcDown) => bus.set(v, now, v > bus.target ? tcUp : tcDown);
    const pre = st.chapter === 0 && !this.lit && st.igniteT < 0;
    const dim = T.dim;
    up(L.organ, T.organ * MIX.organ * dim, 1.0, 1.4);
    L.organ.lp.set(clamp(T.bright * (1 + 0.15 * T.tilt) * dim, 200, 9000), now, 0.5);
    up(L.choir, T.choir * MIX.choir * dim, 1.4, 1.6);
    L.choir.forms.forEach((k, i) => k.set(lerp([300, 870, 2240][i], [730, 1090, 2440][i], T.vowel), now, 0.8));
    up(L.halo, T.pad * MIX.halo * dim, 1.8, 1.8);
    up(L.drone, T.drone * MIX.drone, pre ? 0.15 : 0.8, pre ? 0.3 : 1.2);
    L.drone.lp.set(T.droneCut, now, pre ? 0.1 : 0.6);
    if (L.drone.rk) for (const k of L.drone.rk) k.set(T.droneRise, now, 0.15);
    L.drone.rise = T.droneRise;
    up(L.cloud, T.cloud * MIX.cloud, pre ? 0.2 : 1, 1.2);
    L.cloud.lp.set(clamp(T.droneCut * 2.2, 120, 3000), now, 0.3);
    up(L.air, T.air * MIX.air, 0.25, 0.6);
    this.cluster.update(now, Object.assign({ q: pre ? st.charge : 0 }, T, { cluster: T.cluster * MIX.cluster }));
    up(B.piano, T.piano * MIX.piano, 0.3, 1.2);
    up(B.bells, T.sparkle > 0 ? MIX.bells * (0.55 + 0.45 * T.sparkle) : 0, 0.3, 1.5);
    up(B.heart, T.heart * MIX.heart, 0.2, 0.4);
    up(B.tick, T.tick * MIX.tick, 0.5, 0.8);
    this.kBend.set(T.bend, now, 0.35);
    m.dip(T.dip, T.master, now);
    // wake / sleep layers, then schedule the next ~quarter second of notes
    L.organL.wake(now); L.choirL.wake(now); L.haloL.wake(now);
    L.drone.wake(now); L.cloud.wake(now); L.air.wake(now);
    this.seq.setProg(T.prog, now);
    this.seq.run(now, 0.25, T);
    // idle work: the hall impulse, then instrument buffers
    if (!m.hallStep(this.rng, 0.5)) return;
    if (this.bank.queue.length) this.bank.work(this.cfg.work);
  }

  cue(name, st) {
    // render what the hit needs first, then read the clock, so a slow phone
    // never schedules the ignition in the past
    if (name === 'ignite' && !this.lit) { this.bank.get('swell'); for (const m of [86, 88, 90, 93]) this.bank.get('bell', m); }
    else if (name === 'brand') { for (const m of [81, 86, 90, 93]) this.bank.get('bell', m); }
    else if (name === 'click' || name === 'poster') this.bank.get('tick');
    const t = this.ctx.currentTime + 0.01;
    const c = this.cues, rl = (gap) => c.ok(name, gap, t);
    if (name === 'ignite') {
      if (this.lit) return;
      this.lit = true;
      this.igniteAt = t;
      this.cluster.kill(t);
      this.L.drone.k.go(0, t, 0.06); this.L.drone.target = 0;
      this.B.heart.k.go(0, t, 0.03); this.B.heart.target = 0;
      this.L.cloud.k.go(0, t + 0.3, 0.5); this.L.cloud.target = 0;
      c.ignite(t);
    } else if (name === 'horizon') {
      if (this.darkAt >= 0 || !rl(1)) return;
      c.horizon(t);
      this.darkAt = t;
      this.master.kDuck.go(0, t + 0.14, 0.02);
    } else if (name === 'whoosh') {
      if (rl(0.6)) c.whoosh(t, (st && st.velocity < 0) ? -1 : 1);
    } else if (name === 'brand') {
      if (!rl(0.4)) return;
      c.brand(t);
      this.brandAt = t;
      this.seq.motifAt = t + 0.45;
    } else if (name === 'click') { if (rl(0.05)) c.click(t); }
    else if (name === 'hover') { if (rl(0.08)) c.hover(t); }
    else if (name === 'poster') { if (rl(0.5)) c.poster(t); }
  }

  // active voices, for the harness and window.__debug
  stats() {
    const L = this.L, now = this.ctx.currentTime;
    return {
      organ: L.organL.voices.length, choir: L.choirL.voices.length * this.cfg.choirUnison, halo: L.haloL.voices.length,
      cluster: this.cluster.count(), shots: this.shots.count(now), prog: this.seq.name,
      drone: !!L.drone.nodes, hall: !!this.master.hall, bank: this.bank.map.size,
    };
  }
}
void PROG; void num;
